import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Theme/Common'
import { Icon } from '@mobi/component-library/Common/V2/Icon/Icon'
import { SvgLazy } from '@mobi/component-library/Common/Svg/SvgLazy/SvgLazy'
import { formatUnicodeToEmoji } from '@core/Areas/BetLoop/helpers/stringFormat'
import type { BetLoopProfile } from '@core/Areas/BetLoop/types'

type Size = 'xs' | 'sm' | 'lg' | 'profile-card'

export const ProfileAvatar: React.FC<
  Pick<BetLoopProfile, 'avatar' | 'color'> & { size?: Size; shouldUseBg?: boolean }
> = ({
  avatar = 'U+1F34B',
  color = 'linear-gradient(180deg, #FFF, #A0E528)',
  size = 'sm',
  shouldUseBg = true,
}) => {
  const isProfileImage = avatar.startsWith('http')
  const isGiddyUp = avatar === 'GIDDYUP'
  const shouldDisplayCheck = (isProfileImage || isGiddyUp) && size === 'sm'

  const background = shouldUseBg ? (isProfileImage || isGiddyUp ? colors.white : color) : undefined

  return (
    <WrapperStyled
      data-testid='ProfileAvatar'
      className={`bet-loop__avatar--${size}`}
      style={{ background }}
    >
      <div>
        {isProfileImage ? (
          <img src={avatar} alt='Image of Expert Hub Tipster from Contentful' />
        ) : isGiddyUp ? (
          <SvgLazy width='100%' name='GiddyUpG' color='' />
        ) : (
          `${formatUnicodeToEmoji(avatar)}`
        )}

        {shouldDisplayCheck && (
          <span className='profile-image__check'>
            <Icon name='SolidCheckVerified02' color={colors.studio[600]} size={spacing.md} />
          </span>
        )}
      </div>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  flexShrink: 0,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  borderRadius: radius.full,
  fontFamily: font.family.primary,
  lineHeight: 1,
  textDecoration: 'none',
  cursor: 'pointer',

  '&.bet-loop__avatar--xs': { width: '2.4rem', height: '2.4rem', fontSize: '1.4rem' },
  '&.bet-loop__avatar--sm': { width: '3.6rem', height: '3.6rem', fontSize: '2.4rem' },
  '&.bet-loop__avatar--lg': { width: '12rem', height: '12rem', fontSize: '8rem' },
  '&.bet-loop__avatar--profile-card': {
    width: '14rem',
    height: '14rem',
    fontSize: '11rem',

    '@media (max-width: 360px)': {
      width: '13rem',
      height: '13rem',
      fontSize: '8rem',
    },

    '@media (max-width: 320px)': {
      width: '12rem',
      height: '12rem',
      fontSize: '8rem',
    },

    '> div > img': {
      overflow: 'visible',
    },
  },

  '> div': {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '> img': {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: radius.full,
    },

    '> svg': {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%',
    },

    '> span.profile-image__check': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: spacing.md,
      height: spacing.md,
      position: 'absolute',
      right: 0,
      bottom: 0,
      transform: 'translate(20%, 20%)',
      filter:
        'drop-shadow(1px 0 0 white) drop-shadow(-1px 0 0 white) drop-shadow(0 1px 0 white) drop-shadow(0 -1px 0 white)',
    },
  },
})
